import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Divider, Stepper, Step, StepLabel, StepContent, Box, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Switch, Link,
} from '@mui/material';
import { Add, Bolt, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { epcDiscomApi, type EpcDiscomCase, type DiscomStage, type StageData, type DiscomActivity } from '../../api/epcDiscom.api';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { sitesApi } from '../../api/sites.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const SERVICE_TYPES = ['New Connection', 'Load Enhancement', 'Load Reduction', 'Single Phase to Three Phase', 'LT to HT Conversion', 'Meter Shifting', 'Name Change', 'Tariff Change', 'Other'];
const CONNECTION_CATEGORIES = ['Commercial', 'Industrial', 'EV Charging Station', 'Other'];
const APPLICANT_TYPES = ['Company', 'Proprietorship', 'Partnership', 'Individual', 'Other'];
const PROPERTY_OWNERSHIP = ['Owned', 'Rented', 'Leased', 'Client-Owned'];
const PRIORITIES = ['Normal', 'High', 'Critical'];
const human = (s: string) => s.replace(/_/g, ' ');

// Lifecycle stages after Client/Project/Site mapping (done at creation).
const STAGES: Array<{ key: DiscomStage; label: string }> = [
  { key: 'mapping', label: 'Client / Project / Site' },
  { key: 'documents', label: 'Documents & Application' },
  { key: 'follow_up', label: 'Online Application & Document Submission' },
  { key: 'demand_note', label: 'DISCOM Site Survey & JE/AE Approval' },
  { key: 'payment', label: 'Estimate Preparation & Submission' },
  { key: 'meter', label: 'Agreement & Work Order' },
  { key: 'energization', label: 'BOQ Process' },
  { key: 'closed', label: 'Tender Process' },
  { key: 'contractor', label: 'Contractor & DTR Installation' },
  { key: 'golive', label: 'Meterization & Go-Live' },
];
const DOC_STATUSES = ['Documents Pending', 'Documents Partially Received', 'Documents Under Verification', 'Documents Complete', 'Ready for Application'];
const stageIndex = (s: DiscomStage) => Math.max(0, STAGES.findIndex((x) => x.key === s));
// Field stages that freeze after a save (mapping/documents/golive stay open).
const FREEZABLE = new Set<DiscomStage>(['follow_up', 'demand_note', 'payment', 'meter', 'energization', 'closed', 'contractor']);
function stageFrozen(stage: DiscomStage, c: EpcDiscomCase, editable: boolean, editStages: Set<string>): boolean {
  if (!editable || !FREEZABLE.has(stage) || editStages.has(stage)) return false;
  return (c.activity ?? []).some((a) => a.type === 'save' && a.stage === stage); // saved at least once → frozen
}
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'resolved' ? 'success' : s === 'in_progress' ? 'info' : s === 'pending_docs' ? 'warning' : s === 'rejected' ? 'error' : 'default';

export function EpcDiscomPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['epc-discom', page, limit], queryFn: () => epcDiscomApi.list({ page, limit }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-discom'] });

  const columns: Column<EpcDiscomCase>[] = [
    { key: 'caseNumber', label: 'Case', render: (r) => r.caseNumber },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'projectName', label: 'Project', render: (r) => r.projectName ? `${r.projectName}${r.projectCode ? ` (${r.projectCode})` : ''}` : '—' },
    { key: 'siteName', label: 'Site', render: (r) => r.siteName ? `${r.siteName}${r.siteCode ? ` (${r.siteCode})` : ''}` : '—' },
    { key: 'serviceType', label: 'Service', render: (r) => r.serviceType ?? '—' },
    { key: 'discomName', label: 'DISCOM', render: (r) => r.discomName ?? '—' },
    { key: 'expectedResolution', label: 'Expected', render: (r) => (r.expectedResolution ? new Date(r.expectedResolution).toLocaleDateString('en-IN') : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={human(r.status)} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="DISCOM Applications" subtitle="Track DISCOM applications (new connection, load enhancement, metering, tariff, compliance) per site — filed → in progress → resolved."
        action={hasPermission('am_discom.create') && (
          <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setCreateOpen(true); }}>New Application</Button>
        )} />

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Bolt fontSize="small" /></IconButton></Tooltip>} />

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />}
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function CreateDialog({ onClose, onDone, onError, error }: { onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null }) {
  const [clientId, setClientId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [form, setForm] = useState<Record<string, string>>({
    discomName: '', serviceType: '', currentLoadKw: '', requiredLoadKw: '', expectedResolution: '', notes: '',
    // basic
    siteAddress: '', state: '', city: '', pincode: '', mapsLink: '', clientContactPerson: '', clientContactNumber: '', clientEmail: '',
    requestDate: '', requiredCompletionDate: '', priority: 'Normal', projectManager: '', discomCoordinator: '',
    // connection
    connectionCategory: '', existingConnection: 'no', existingConsumerNumber: '', existingContractDemandKva: '', requiredContractDemandKva: '', existingBillUrl: '', clientRequirement: '',
    // applicant
    applicantName: '', companyName: '', applicantType: '', applicantPan: '', applicantGst: '', signatoryName: '', signatoryMobile: '', signatoryEmail: '',
    propertyOwnership: '', propertyOwnerName: '', ownerNocRequired: 'no',
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 500, status: 'active' }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 1000 }) });
  const sites = useQuery({ queryKey: ['sites', 'all'], queryFn: () => sitesApi.list({ limit: 1000 }) });
  const clientProjects = (projects.data?.data ?? []).filter((p) => p.clientId === clientId);
  const projectSites = (sites.data?.data ?? []).filter((s) => s.projectId === projectId);
  const hasExisting = form.existingConnection === 'yes';

  const s = (k: string) => (form[k] || undefined);
  const nOrU = (k: string) => (form[k] ? Number(form[k]) : undefined);
  const mut = useMutation({
    mutationFn: () => epcDiscomApi.create({
      projectId: Number(projectId), siteId: siteId ? Number(siteId) : undefined, clientId: clientId ? Number(clientId) : undefined,
      discomName: s('discomName'), serviceType: s('serviceType'),
      currentLoadKw: nOrU('currentLoadKw'), requiredLoadKw: nOrU('requiredLoadKw'),
      expectedResolution: s('expectedResolution'), notes: s('notes'),
      // basic
      siteAddress: s('siteAddress'), state: s('state'), city: s('city'), pincode: s('pincode'), mapsLink: s('mapsLink'),
      clientContactPerson: s('clientContactPerson'), clientContactNumber: s('clientContactNumber'), clientEmail: s('clientEmail'),
      requestDate: s('requestDate'), requiredCompletionDate: s('requiredCompletionDate'), priority: s('priority'),
      projectManager: s('projectManager'), discomCoordinator: s('discomCoordinator'),
      // connection
      connectionCategory: s('connectionCategory'), existingConnection: hasExisting,
      existingConsumerNumber: hasExisting ? s('existingConsumerNumber') : undefined,
      existingContractDemandKva: hasExisting ? nOrU('existingContractDemandKva') : undefined,
      requiredContractDemandKva: nOrU('requiredContractDemandKva'), existingBillUrl: hasExisting ? s('existingBillUrl') : undefined,
      clientRequirement: s('clientRequirement'),
      // applicant
      applicantName: s('applicantName'), companyName: s('companyName'), applicantType: s('applicantType'),
      applicantPan: s('applicantPan'), applicantGst: s('applicantGst'),
      signatoryName: s('signatoryName'), signatoryMobile: s('signatoryMobile'), signatoryEmail: s('signatoryEmail'),
      propertyOwnership: s('propertyOwnership'), propertyOwnerName: s('propertyOwnerName'), ownerNocRequired: form.ownerNocRequired === 'yes',
    }),
    onSuccess: (c) => onDone(c.id),
    onError: (e) => onError(extractApiError(e)),
  });

  const yesNo = (k: string, label: string) => (
    <TextField select size="small" label={label} value={form[k]} onChange={(e) => set(k, e.target.value)} sx={{ minWidth: 160 }}>
      <MenuItem value="yes">Yes</MenuItem><MenuItem value="no">No</MenuItem>
    </TextField>
  );

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>New DISCOM Application</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Basic Details · Client → Project → Site</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Client *" value={clientId} sx={{ minWidth: 220 }}
              onChange={(e) => { setClientId(e.target.value ? Number(e.target.value) : ''); setProjectId(''); setSiteId(''); }}>
              {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Project *" value={projectId} disabled={!clientId} sx={{ minWidth: 240 }}
              onChange={(e) => { setProjectId(e.target.value ? Number(e.target.value) : ''); setSiteId(''); }}>
              {clientProjects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectCode} — {p.projectName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Site" value={siteId} disabled={!projectId} sx={{ minWidth: 220 }}
              onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}>
              {projectSites.map((si) => <MenuItem key={si.id} value={si.id}>{si.siteName} ({si.siteCode})</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Site Address" value={form.siteAddress} onChange={(e) => set('siteAddress', e.target.value)} sx={{ minWidth: 260 }} />
            <TextField size="small" label="City" value={form.city} onChange={(e) => set('city', e.target.value)} sx={{ minWidth: 130 }} />
            <TextField size="small" label="State" value={form.state} onChange={(e) => set('state', e.target.value)} sx={{ minWidth: 130 }} />
            <TextField size="small" label="Pin Code" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} sx={{ minWidth: 110 }} />
            <TextField size="small" label="Google Maps Link" value={form.mapsLink} onChange={(e) => set('mapsLink', e.target.value)} sx={{ minWidth: 220 }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Client Contact Person" value={form.clientContactPerson} onChange={(e) => set('clientContactPerson', e.target.value)} sx={{ minWidth: 200 }} />
            <TextField size="small" label="Client Contact Number" value={form.clientContactNumber} onChange={(e) => set('clientContactNumber', e.target.value)} sx={{ minWidth: 180 }} />
            <TextField size="small" label="Client Email" value={form.clientEmail} onChange={(e) => set('clientEmail', e.target.value)} sx={{ minWidth: 200 }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" type="date" label="Request Date" InputLabelProps={{ shrink: true }} value={form.requestDate} onChange={(e) => set('requestDate', e.target.value)} />
            <TextField size="small" type="date" label="Required Completion Date" InputLabelProps={{ shrink: true }} value={form.requiredCompletionDate} onChange={(e) => set('requiredCompletionDate', e.target.value)} />
            <TextField select size="small" label="Priority" value={form.priority} onChange={(e) => set('priority', e.target.value)} sx={{ minWidth: 130 }}>
              {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Assigned Project Manager" value={form.projectManager} onChange={(e) => set('projectManager', e.target.value)} sx={{ minWidth: 200 }} />
            <TextField size="small" label="Assigned DISCOM Coordinator" value={form.discomCoordinator} onChange={(e) => set('discomCoordinator', e.target.value)} sx={{ minWidth: 220 }} />
          </Stack>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Connection Details</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Service Type" value={form.serviceType} onChange={(e) => set('serviceType', e.target.value)} sx={{ minWidth: 220 }}>
              {SERVICE_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField size="small" label="DISCOM Name" value={form.discomName} onChange={(e) => set('discomName', e.target.value)} sx={{ minWidth: 180 }} />
            <TextField select size="small" label="Connection Category" value={form.connectionCategory} onChange={(e) => set('connectionCategory', e.target.value)} sx={{ minWidth: 200 }}>
              {CONNECTION_CATEGORIES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            {yesNo('existingConnection', 'Existing Connection?')}
          </Stack>
          {hasExisting && (
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Existing Consumer Number" value={form.existingConsumerNumber} onChange={(e) => set('existingConsumerNumber', e.target.value)} sx={{ minWidth: 200 }} />
              <TextField size="small" type="number" label="Existing Contract Demand (kVA)" value={form.existingContractDemandKva} onChange={(e) => set('existingContractDemandKva', e.target.value)} sx={{ minWidth: 200 }} />
              <FileUploadField label="Existing Electricity Bill" accept=".pdf,image/*" value={form.existingBillUrl} onChange={(u) => set('existingBillUrl', u ?? '')} />
            </Stack>
          )}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" type="number" label="Existing Sanctioned Load (kW)" value={form.currentLoadKw} onChange={(e) => set('currentLoadKw', e.target.value)} sx={{ minWidth: 200 }} />
            <TextField size="small" type="number" label="Required Load (kW)" value={form.requiredLoadKw} onChange={(e) => set('requiredLoadKw', e.target.value)} sx={{ minWidth: 160 }} />
            <TextField size="small" type="number" label="Required Contract Demand (kVA)" value={form.requiredContractDemandKva} onChange={(e) => set('requiredContractDemandKva', e.target.value)} sx={{ minWidth: 200 }} />
            <TextField size="small" type="date" label="Expected Resolution" InputLabelProps={{ shrink: true }} value={form.expectedResolution} onChange={(e) => set('expectedResolution', e.target.value)} />
          </Stack>
          <TextField size="small" label="Client Requirement / Remarks" multiline minRows={2} value={form.clientRequirement} onChange={(e) => set('clientRequirement', e.target.value)} />

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Applicant Details</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Applicant Name" value={form.applicantName} onChange={(e) => set('applicantName', e.target.value)} sx={{ minWidth: 200 }} />
            <TextField size="small" label="Company Name" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} sx={{ minWidth: 200 }} />
            <TextField select size="small" label="Applicant Type" value={form.applicantType} onChange={(e) => set('applicantType', e.target.value)} sx={{ minWidth: 170 }}>
              {APPLICANT_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField size="small" label="PAN Number" value={form.applicantPan} onChange={(e) => set('applicantPan', e.target.value.toUpperCase())} sx={{ minWidth: 150 }} />
            <TextField size="small" label="GST Number" value={form.applicantGst} onChange={(e) => set('applicantGst', e.target.value.toUpperCase())} sx={{ minWidth: 170 }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Authorized Signatory Name" value={form.signatoryName} onChange={(e) => set('signatoryName', e.target.value)} sx={{ minWidth: 220 }} />
            <TextField size="small" label="Signatory Mobile" value={form.signatoryMobile} onChange={(e) => set('signatoryMobile', e.target.value)} sx={{ minWidth: 160 }} />
            <TextField size="small" label="Signatory Email" value={form.signatoryEmail} onChange={(e) => set('signatoryEmail', e.target.value)} sx={{ minWidth: 200 }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Property Ownership" value={form.propertyOwnership} onChange={(e) => set('propertyOwnership', e.target.value)} sx={{ minWidth: 180 }}>
              {PROPERTY_OWNERSHIP.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Property Owner Name" value={form.propertyOwnerName} onChange={(e) => set('propertyOwnerName', e.target.value)} sx={{ minWidth: 200 }} />
            {yesNo('ownerNocRequired', 'Owner NOC Required?')}
          </Stack>
          <TextField size="small" label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!projectId || mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose, onChanged, onToast }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: c } = useQuery({ queryKey: ['epc-discom', id], queryFn: () => epcDiscomApi.get(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-discom', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { setViewStep(null); refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));
  // `viewStep` lets the user click a stage to review it; otherwise the current (persisted) stage shows.
  const [viewStep, setViewStep] = useState<number | null>(null);
  // Stages the user has explicitly reopened for editing this session (a saved field-stage is frozen otherwise).
  const [editStages, setEditStages] = useState<Set<string>>(new Set());
  const reopenStage = (k: string) => setEditStages((s) => new Set(s).add(k));
  if (!c) return null;
  const canEdit = hasPermission('am_discom.update');
  const editable = canEdit && c.status !== 'resolved'; // completed apps are read-only until reopened
  const activeStep = viewStep ?? stageIndex(c.stage);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {c.caseNumber} <Chip size="small" sx={{ ml: 1 }} label={human(c.status)} color={statusColor(c.status)} />
        <Typography variant="body2" color="text.secondary">
          {[c.clientName, c.projectName ? `${c.projectName}${c.projectCode ? ` (${c.projectCode})` : ''}` : null, c.siteName].filter(Boolean).join(' · ') || '—'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          <b>Service:</b> {c.serviceType ?? '—'} · <b>DISCOM:</b> {c.discomName ?? '—'} · <b>Load:</b> {c.currentLoadKw ?? '—'} → {c.requiredLoadKw ?? '—'} kW
          {c.expectedResolution ? ` · Expected: ${fmtDate(c.expectedResolution)}` : ''}
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          {STAGES.map((st, idx) => (
            <Step key={st.key} completed={idx < stageIndex(c.stage) || c.status === 'resolved'}>
              <StepLabel onClick={() => setViewStep(idx)} sx={{ cursor: 'pointer' }}
                optional={idx === stageIndex(c.stage) && c.status !== 'resolved' ? <Typography variant="caption" color="primary">current stage</Typography> : undefined}>
                {st.label}
              </StepLabel>
              <StepContent>
                <StageBody stage={st.key} c={c}
                  canEdit={editable && !stageFrozen(st.key, c, editable, editStages)}
                  frozen={stageFrozen(st.key, c, editable, editStages)}
                  onReopenStage={() => reopenStage(st.key)}
                  onSave={(data) => { setEditStages(new Set()); act(() => epcDiscomApi.setStage(id, { stage: st.key, data }), `${st.label} saved`); }}
                  onAdvance={(next) => act(() => epcDiscomApi.setStage(id, { stage: next }), `Moved to ${human(next)}`)}
                  onUpdateDoc={(docId, body) => act(() => epcDiscomApi.updateDocument(id, docId, body), 'Document updated')}
                  onAddDoc={(docName) => act(() => epcDiscomApi.addDocument(id, { docName }), 'Document added')}
                  onRemoveDoc={(docId) => act(() => epcDiscomApi.removeDocument(id, docId), 'Document removed')}
                  onSetDocStatus={(status) => act(() => epcDiscomApi.update(id, { documentStatus: status }), 'Document status updated')}
                />
                <StageFooter activity={(c.activity ?? []).filter((a) => a.stage === st.key)} canEdit={editable}
                  onUpload={(fileUrl, fileName) => act(() => epcDiscomApi.addStageDocument(id, { stage: st.key, fileUrl, fileName }), 'Supporting document uploaded')} />
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions>
        {canEdit && c.status === 'resolved' && (
          c.canReopen
            ? <Button color="warning" variant="outlined" onClick={() => act(() => epcDiscomApi.reopen(id), 'Application reopened')}>Reopen (within 24h)</Button>
            : <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Reopen window (24h) has passed</Typography>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function nextStage(cur: DiscomStage): DiscomStage {
  const i = STAGES.findIndex((s) => s.key === cur);
  return STAGES[Math.min(STAGES.length - 1, i + 1)].key;
}

/** Per-stage read/edit body inside the stepper. */
/** Activity log (who/when) + supporting-document upload, shown under every stage. */
function StageFooter({ activity, canEdit, onUpload }: { activity: DiscomActivity[]; canEdit: boolean; onUpload: (fileUrl: string, fileName: string | null) => void }) {
  const docs = activity.filter((a) => a.type === 'document');
  const fmtDT = (s: string) => new Date(s).toLocaleString('en-IN');
  return (
    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #ddd' }}>
      {docs.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Supporting documents</Typography>
          {docs.map((a) => (
            <Typography key={a.id} variant="body2"><Link href={a.fileUrl ?? '#'} target="_blank" rel="noreferrer">{a.fileName || 'document'}</Link> <Typography variant="caption" color="text.secondary" component="span">· {a.by ?? '—'} · {fmtDT(a.at)}</Typography></Typography>
          ))}
        </Box>
      )}
      {canEdit && (
        <Box sx={{ mb: 1 }}>
          <FileUploadField label="Upload supporting document" accept=".pdf,image/*,.xls,.xlsx,.doc,.docx" value="" onChange={(u) => { if (u) onUpload(u, decodeURIComponent(u.split('/').pop() ?? '') || null); }} />
        </Box>
      )}
      {activity.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">Log</Typography>
          {activity.slice().reverse().map((a) => (
            <Typography key={a.id} variant="caption" display="block" color="text.secondary">
              {a.type === 'save' ? 'Saved' : a.type === 'reopen' ? 'Reopened' : 'Document added'} by {a.by ?? '—'} · {fmtDT(a.at)}
              {a.type === 'save' && a.note && a.note !== 'Saved (no field changes)' ? ` — ${a.note}` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function StageBody({ stage, c, canEdit, frozen, onSave, onAdvance, onReopenStage, onUpdateDoc, onAddDoc, onRemoveDoc, onSetDocStatus }: {
  stage: DiscomStage; c: EpcDiscomCase; canEdit: boolean; frozen: boolean;
  onSave: (data: StageData) => void; onAdvance: (next: DiscomStage) => void; onReopenStage: () => void;
  onUpdateDoc: (docId: number, body: { isRequired?: boolean; fileUrl?: string | null; verification?: 'pending' | 'verified' | 'rejected'; rejectionReason?: string | null; remarks?: string | null }) => void;
  onAddDoc: (docName: string) => void;
  onRemoveDoc: (docId: number) => void;
  onSetDocStatus: (status: string) => void;
}) {
  const [d, setD] = useState<StageData>({});
  const [newDoc, setNewDoc] = useState('');
  const g = <K extends keyof StageData>(k: K, fallback: EpcDiscomCase[keyof EpcDiscomCase]) => (d[k] !== undefined ? d[k] : fallback) as string;
  const advanceBtn = (canEdit || frozen) && stage !== 'golive' && (
    <Button size="small" onClick={() => onAdvance(nextStage(stage))}>Next: {STAGES[stageIndex(stage) + 1]?.label}</Button>
  );
  // Frozen (already saved) → fields locked; show "Reopen to edit" instead of Save. Editable → Save.
  const actions = (
    <Stack direction="row" spacing={1}>
      {frozen
        ? <Button size="small" variant="outlined" color="warning" onClick={onReopenStage}>Reopen to edit</Button>
        : (canEdit && <Button size="small" variant="contained" onClick={() => onSave(d)}>Save</Button>)}
      {advanceBtn}
    </Stack>
  );

  if (stage === 'documents') {
    const docs = c.documents ?? [];
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Application number" value={g('applicationNumber', c.applicationNumber)} onChange={(e) => setD({ ...d, applicationNumber: e.target.value })} disabled={!canEdit} />
          <TextField size="small" type="date" label="Application date" InputLabelProps={{ shrink: true }} value={g('applicationDate', c.applicationDate)} onChange={(e) => setD({ ...d, applicationDate: e.target.value })} disabled={!canEdit} />
          <TextField select size="small" label="Document Status" value={c.documentStatus ?? ''} onChange={(e) => onSetDocStatus(e.target.value)} disabled={!canEdit} sx={{ minWidth: 220 }}>
            {DOC_STATUSES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          {canEdit && <Button size="small" variant="contained" onClick={() => onSave(d)}>Save details</Button>}
        </Stack>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Document</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Required</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Verification</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                {canEdit && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>{doc.docName}{doc.isCustom && <Chip size="small" variant="outlined" label="custom" sx={{ ml: 0.5 }} />}</TableCell>
                  <TableCell align="center"><Switch size="small" checked={doc.isRequired} disabled={!canEdit} onChange={(e) => onUpdateDoc(doc.id, { isRequired: e.target.checked })} /></TableCell>
                  <TableCell>
                    {doc.fileUrl ? <Link href={doc.fileUrl} target="_blank" rel="noreferrer">View</Link> : <Chip size="small" color="warning" variant="outlined" label="pending" />}
                    {canEdit && <Box sx={{ mt: 0.5 }}><FileUploadField label="" accept=".pdf,image/*" value={doc.fileUrl ?? ''} onChange={(u) => onUpdateDoc(doc.id, { fileUrl: u ?? null })} /></Box>}
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" value={doc.verification} disabled={!canEdit || !doc.fileUrl}
                      onChange={(e) => { const v = e.target.value as 'pending' | 'verified' | 'rejected'; const reason = v === 'rejected' ? (window.prompt('Rejection reason?') ?? '') : null; onUpdateDoc(doc.id, { verification: v, rejectionReason: reason }); }} sx={{ minWidth: 130 }}>
                      <MenuItem value="pending">Pending</MenuItem><MenuItem value="verified">Verified</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                    {doc.verification === 'rejected' && doc.rejectionReason && <Typography variant="caption" color="error" display="block">{doc.rejectionReason}</Typography>}
                  </TableCell>
                  <TableCell>
                    <TextField size="small" defaultValue={doc.remarks ?? ''} disabled={!canEdit} placeholder="—" onBlur={(e) => { if (e.target.value !== (doc.remarks ?? '')) onUpdateDoc(doc.id, { remarks: e.target.value || null }); }} sx={{ minWidth: 120 }} />
                  </TableCell>
                  {canEdit && <TableCell>{doc.isCustom && <Tooltip title="Remove"><IconButton size="small" onClick={() => onRemoveDoc(doc.id)}><Delete fontSize="inherit" /></IconButton></Tooltip>}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {canEdit && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" label="Add custom document" value={newDoc} onChange={(e) => setNewDoc(e.target.value)} sx={{ minWidth: 260 }} />
            <Button size="small" startIcon={<Add />} disabled={!newDoc.trim()} onClick={() => { onAddDoc(newDoc.trim()); setNewDoc(''); }}>Add</Button>
            <Box flex={1} />
            {advanceBtn}
          </Stack>
        )}
      </Stack>
    );
  }

  if (stage === 'mapping') {
    return <Typography variant="body2" color="text.secondary">{[c.clientName, c.projectName, c.siteName].filter(Boolean).join(' · ') || '—'} — filed {fmtDate(c.filedDate)}. {advanceBtn}</Typography>;
  }

  if (stage === 'follow_up') {
    // Online Application & Document Submission
    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">Online Application</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <TextField size="small" type="date" label="Application Date" InputLabelProps={{ shrink: true }} value={g('onlineApplicationDate', c.onlineApplicationDate)} onChange={(e) => setD({ ...d, onlineApplicationDate: e.target.value })} disabled={!canEdit} />
            <FileUploadField label="Upload online application" accept=".pdf,image/*" value={g('onlineApplicationUrl', c.onlineApplicationUrl)} onChange={(u) => setD({ ...d, onlineApplicationUrl: u ?? '' })} />
          </Stack>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Demand Note Payment</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <TextField size="small" type="date" label="Demand Note Paid Date" InputLabelProps={{ shrink: true }} value={g('demandNotePaidDate', c.demandNotePaidDate)} onChange={(e) => setD({ ...d, demandNotePaidDate: e.target.value })} disabled={!canEdit} />
            <FileUploadField label="Upload demand note receipt" accept=".pdf,image/*" value={g('demandNoteReceiptUrl', c.demandNoteReceiptUrl)} onChange={(u) => setD({ ...d, demandNoteReceiptUrl: u ?? '' })} />
          </Stack>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Hard Copy of Documents — Submission to JE / AE</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <TextField select size="small" label="Submission Status" value={g('hardcopySubmissionStatus', c.hardcopySubmissionStatus)} onChange={(e) => setD({ ...d, hardcopySubmissionStatus: e.target.value })} disabled={!canEdit} sx={{ minWidth: 160 }}>
              <MenuItem value="Submitted">Submitted</MenuItem><MenuItem value="Pending">Pending</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Submit Date" InputLabelProps={{ shrink: true }} value={g('hardcopySubmitDate', c.hardcopySubmitDate)} onChange={(e) => setD({ ...d, hardcopySubmitDate: e.target.value })} disabled={!canEdit} />
          </Stack>
        </Box>
        {actions}
      </Stack>
    );
  }

  if (stage === 'closed') {
    // Tender Process
    const tn = (k: keyof StageData, label: string, fallback: string | null) => (
      <TextField select size="small" label={label} value={g(k, fallback)} onChange={(e) => setD({ ...d, [k]: e.target.value })} disabled={!canEdit} sx={{ minWidth: 220 }}>
        <MenuItem value="Yes">Yes</MenuItem><MenuItem value="No">No</MenuItem>
      </TextField>
    );
    return (
      <Stack spacing={2}>
        {tn('tenderSanction', 'Tender Sanction', c.tenderSanction)}
        {tn('tenderAward', 'Tender Award', c.tenderAward)}
        {actions}
      </Stack>
    );
  }

  if (stage === 'contractor') {
    // Contractor & DTR Installation
    return (
      <Stack spacing={2}>
        <TextField select size="small" label="Contractor Finalisation & DTR Installation" value={g('contractorDtr', c.contractorDtr)} onChange={(e) => setD({ ...d, contractorDtr: e.target.value })} disabled={!canEdit} sx={{ minWidth: 320 }}>
          <MenuItem value="Yes">Yes</MenuItem><MenuItem value="No">No</MenuItem>
        </TextField>
        <TextField select size="small" label="Status of Work" value={g('workStatus', c.workStatus)} onChange={(e) => setD({ ...d, workStatus: e.target.value })} disabled={!canEdit} sx={{ minWidth: 240 }}>
          <MenuItem value="Not started">Not started</MenuItem><MenuItem value="In-Process">In-Process</MenuItem><MenuItem value="Completed">Completed</MenuItem>
        </TextField>
        {actions}
      </Stack>
    );
  }

  if (stage === 'golive') {
    // Meterization & Go-Live — completing Go-Live = Yes closes the application.
    const meterYes = g('meterizationStatus', c.meterizationStatus) === 'Yes';
    const goliveYes = g('goliveStatus', c.goliveStatus) === 'Yes';
    return (
      <Stack spacing={2}>
        {c.status === 'resolved' && <Alert severity="success">Application completed{c.resolvedDate ? ` on ${fmtDate(c.resolvedDate)}` : ''}.</Alert>}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Meterization of DTR" value={g('meterizationStatus', c.meterizationStatus)} onChange={(e) => setD({ ...d, meterizationStatus: e.target.value })} disabled={!canEdit} sx={{ minWidth: 200 }}>
            <MenuItem value="Yes">Yes</MenuItem><MenuItem value="No">No</MenuItem>
          </TextField>
          {meterYes && <TextField size="small" type="date" label="Meterization Date" InputLabelProps={{ shrink: true }} value={g('meterizationDate', c.meterizationDate)} onChange={(e) => setD({ ...d, meterizationDate: e.target.value })} disabled={!canEdit} />}
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Go-Live" value={g('goliveStatus', c.goliveStatus)} onChange={(e) => setD({ ...d, goliveStatus: e.target.value })} disabled={!canEdit} sx={{ minWidth: 200 }}>
            <MenuItem value="Yes">Yes</MenuItem><MenuItem value="No">No</MenuItem>
          </TextField>
          {goliveYes && <TextField size="small" type="date" label="Go-Live Date" InputLabelProps={{ shrink: true }} value={g('goliveDate', c.goliveDate)} onChange={(e) => setD({ ...d, goliveDate: e.target.value })} disabled={!canEdit} />}
        </Stack>
        {canEdit && <Button size="small" variant="contained" color="success" onClick={() => onSave(d)}>Save {goliveYes ? '& Complete application' : ''}</Button>}
      </Stack>
    );
  }

  if (stage === 'demand_note') {
    // DISCOM Team Site Survey & JE/AE Approval
    const surveyDone = g('discomSurveyStatus', c.discomSurveyStatus) === 'Completed';
    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">DISCOM Team Site Survey</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <TextField select size="small" label="Site Survey" value={g('discomSurveyStatus', c.discomSurveyStatus)} onChange={(e) => setD({ ...d, discomSurveyStatus: e.target.value })} disabled={!canEdit} sx={{ minWidth: 160 }}>
              <MenuItem value="Pending">Pending</MenuItem><MenuItem value="Completed">Completed</MenuItem>
            </TextField>
            {surveyDone && <TextField size="small" type="date" label="Site Survey Date" InputLabelProps={{ shrink: true }} value={g('discomSurveyDate', c.discomSurveyDate)} onChange={(e) => setD({ ...d, discomSurveyDate: e.target.value })} disabled={!canEdit} />}
          </Stack>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Signature & Approval of JE / AE</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
            <TextField select size="small" label="JE/AE Approval" value={g('jeAeApprovalStatus', c.jeAeApprovalStatus)} onChange={(e) => setD({ ...d, jeAeApprovalStatus: e.target.value })} disabled={!canEdit} sx={{ minWidth: 160 }}>
              <MenuItem value="Pending">Pending</MenuItem><MenuItem value="Approved">Approved</MenuItem>
            </TextField>
          </Stack>
        </Box>
        {actions}
      </Stack>
    );
  }

  if (stage === 'payment') {
    // Estimate Preparation & Submission
    const pc = (k: keyof StageData, label: string, fallback: string | null) => (
      <TextField select size="small" label={label} value={g(k, fallback)} onChange={(e) => setD({ ...d, [k]: e.target.value })} disabled={!canEdit} sx={{ minWidth: 320 }}>
        <MenuItem value="Pending">Pending</MenuItem><MenuItem value="Completed">Completed</MenuItem>
      </TextField>
    );
    return (
      <Stack spacing={2}>
        {pc('estimateFramingStatus', 'Online Estimate Framing', c.estimateFramingStatus)}
        {pc('estimateSubmissionStatus', 'Estimate Submission to Executive Engineer', c.estimateSubmissionStatus)}
        {pc('estimateVerificationStatus', 'Verification of Papers, Estimate & Land Papers by EE', c.estimateVerificationStatus)}
        {actions}
      </Stack>
    );
  }

  if (stage === 'meter') {
    // Agreement & Work Order
    const yp = (k: keyof StageData, label: string, fallback: string | null) => (
      <TextField select size="small" label={label} value={g(k, fallback)} onChange={(e) => setD({ ...d, [k]: e.target.value })} disabled={!canEdit} sx={{ minWidth: 280 }}>
        <MenuItem value="Yes">Yes</MenuItem><MenuItem value="Pending">Pending</MenuItem>
      </TextField>
    );
    return (
      <Stack spacing={2}>
        {yp('agreementFinalisation', 'Agreement Finalisation', c.agreementFinalisation)}
        {yp('workOrderFinalisation', 'Work Order Finalisation', c.workOrderFinalisation)}
        {actions}
      </Stack>
    );
  }

  // Stage 7 — BOQ Process (last field stage)
  const yn = (k: keyof StageData, label: string, fallback: string | null) => (
    <TextField select size="small" label={label} value={g(k, fallback)} onChange={(e) => setD({ ...d, [k]: e.target.value })} disabled={!canEdit} sx={{ minWidth: 300 }}>
      <MenuItem value="Yes">Yes</MenuItem><MenuItem value="No">No</MenuItem>
    </TextField>
  );
  return (
    <Stack spacing={2}>
      {yn('boqReady', 'BOQ Ready', c.boqReady)}
      {yn('boqSubmissionEe', 'BOQ Submission to Executive Engineer', c.boqSubmissionEe)}
      {canEdit && (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => onSave(d)}>Save</Button>
          {advanceBtn}
        </Stack>
      )}
    </Stack>
  );
}
